import type { FormEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Session, SupabaseClient, User } from '@supabase/supabase-js'
import { hasSupabaseConfig, supabase } from './lib/supabase'
import { assessClarification, buildClarificationSummary, canEditClarificationState, type ClarificationDraft } from './lib/clarification'
import { buildPortableExport, canTransition, evaluateDoneGate } from './lib/mvp'
import { analyzeIntent, type IntentAnalysis } from './lib/intent-engine'
import './mvp.css'

type Workspace = { id: string; name: string; owner_user_id: string }
type WorkItem = {
  id: string
  title: string
  raw_intent: string
  lifecycle_state: string
  current_work_spec_version_id: string | null
  current_route_plan_version_id: string | null
  updated_at: string
}
type Row = Record<string, unknown>
type Flash = { kind: 'success' | 'error' | 'warning'; text: string } | null

const emptySpec = (intent = ''): ClarificationDraft & {
  acceptance: string; dependencies: string; unknowns: string; approvals: string
} => ({
  goal: intent, audience: '', scope: '', data: '', constraints: '', output: '',
  assumptions: '', outOfScope: '', acceptance: '', dependencies: '', unknowns: '', approvals: '',
})

export default function MvpApp() {
  if (!hasSupabaseConfig || !supabase) {
    return <Status title="iDONE chưa có cấu hình Supabase." detail="Thiết lập VITE_SUPABASE_URL và VITE_SUPABASE_PUBLISHABLE_KEY. Không đưa service-role key vào frontend." />
  }
  return <Auth client={supabase} />
}

function Auth({ client }: { client: SupabaseClient }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    void client.auth.getSession().then(({ data }) => { setSession(data.session); setLoading(false) })
    const { data: { subscription } } = client.auth.onAuthStateChange((_event, value) => {
      setSession(value); setLoading(false)
    })
    return () => subscription.unsubscribe()
  }, [client])
  if (loading) return <Status title="Đang kiểm tra phiên đăng nhập…" />
  if (!session) return <Login client={client} />
  return <WorkspaceShell client={client} user={session.user} />
}

function Login({ client }: { client: SupabaseClient }) {
  const [email, setEmail] = useState('')
  const [flash, setFlash] = useState<Flash>(null)
  const [busy, setBusy] = useState(false)
  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setFlash(null)
    const { error } = await client.auth.signInWithOtp({
      email: email.trim(), options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
    })
    setFlash(error ? { kind: 'error', text: error.message } : { kind: 'success', text: 'Nếu tài khoản đã được mời, hãy kiểm tra email.' })
    setBusy(false)
  }
  return <main className="center-shell"><section className="card auth-card">
    <div className="brand-lockup"><span className="brand-mark">i</span><span className="brand-word">DONE</span></div>
    <p className="eyebrow">Founder-first · Invite-only</p><h1>Từ ý định đến Verified DONE.</h1>
    <p className="muted">Không có đăng ký công khai.</p>
    <form className="stack" onSubmit={submit}><label>Email</label><input type="email" required value={email} onChange={e => setEmail(e.target.value)} />
      <button className="primary" disabled={busy}>{busy ? 'Đang gửi…' : 'Gửi liên kết đăng nhập'}</button></form>
    <FlashView flash={flash} />
  </section></main>
}

function WorkspaceShell({ client, user }: { client: SupabaseClient; user: User }) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [items, setItems] = useState<WorkItem[]>([])
  const [selected, setSelected] = useState<WorkItem | null>(null)
  const [teamOpen, setTeamOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [flash, setFlash] = useState<Flash>(null)

  const load = useCallback(async () => {
    setLoading(true); setFlash(null)
    const membership = await client.from('workspace_memberships').select('workspace_id').eq('user_id', user.id).eq('status', 'active').limit(1)
    if (membership.error) { setFlash({ kind: 'error', text: membership.error.message }); setLoading(false); return }
    const workspaceId = membership.data?.[0]?.workspace_id
    if (!workspaceId) { setLoading(false); return }
    const ws = await client.from('workspaces').select('id,name,owner_user_id').eq('id', workspaceId).single()
    if (ws.error) { setFlash({ kind: 'error', text: ws.error.message }); setLoading(false); return }
    setWorkspace(ws.data as Workspace)
    const works = await client.from('work_items')
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at')
      .eq('workspace_id', workspaceId).order('updated_at', { ascending: false })
    if (works.error) setFlash({ kind: 'error', text: works.error.message })
    else setItems((works.data ?? []) as WorkItem[])
    setLoading(false)
  }, [client, user.id])

  useEffect(() => { void load() }, [load])
  if (loading) return <Status title="Đang tải iDONE…" />
  if (!workspace) return <WorkspaceCreate client={client} user={user} onDone={load} flash={flash} />

  return <div className="app-shell">
    <header className="topbar"><div><div className="brand-lockup compact"><span className="brand-mark">i</span><span className="brand-word">DONE</span></div><p className="workspace-name">{workspace.name}</p></div>
      <div className="topbar-actions"><button className={teamOpen ? 'ghost active-nav' : 'ghost'} onClick={() => { setSelected(null); setTeamOpen(value => !value) }}>Đội ngũ AI</button><span className="pill">PRIVATE BETA</span><button className="ghost" onClick={() => void client.auth.signOut()}>Đăng xuất</button></div></header>
    <main className="workspace-main">
      {selected ? <WorkFlow client={client} user={user} workspace={workspace} initial={selected}
        onBack={() => { setSelected(null); void load() }} /> :
        teamOpen ? <AiTeamManager client={client} user={user} workspace={workspace} items={items}
          onBack={() => { setTeamOpen(false); void load() }}
          onOpenWork={(item) => { setTeamOpen(false); setSelected(item) }} /> :
        <WorkList client={client} user={user} workspace={workspace} items={items}
          onCreated={(work) => { setItems(v => [work, ...v]); setSelected(work) }}
          onOpen={setSelected}
          onOpenTeam={() => setTeamOpen(true)} />}
      <FlashView flash={flash} />
    </main>
  </div>
}


type AiTeamMetric = {
  total: number
  completed: number
  failed: number
  successRate: number | null
  unread: number
}

function AiTeamManager({ client, user, workspace, items, onBack, onOpenWork }: {
  client: SupabaseClient
  user: User
  workspace: Workspace
  items: WorkItem[]
  onBack: () => void
  onOpenWork: (item: WorkItem) => void
}) {
  const [jobs, setJobs] = useState<Row[]>([])
  const [runs, setRuns] = useState<Row[]>([])
  const [readIds, setReadIds] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftTime, setDraftTime] = useState('08:00')
  const [draftTimezone, setDraftTimezone] = useState('Asia/Ho_Chi_Minh')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [flash, setFlash] = useState<Flash>(null)
  const [loading, setLoading] = useState(true)
  const canManage = workspace.owner_user_id === user.id
  const cutoff = useMemo(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), [])
  const timezoneOptions = useMemo(() => {
    const supportedValuesOf = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf
    const values = supportedValuesOf ? supportedValuesOf('timeZone') : [
      'Asia/Ho_Chi_Minh','Asia/Bangkok','Asia/Singapore','Asia/Tokyo',
      'Australia/Sydney','Europe/London','America/New_York','America/Los_Angeles','UTC',
    ]
    return values.includes('Asia/Ho_Chi_Minh') ? values : ['Asia/Ho_Chi_Minh', ...values]
  }, [])

  const formatRuntimeTime = (value: unknown, timezone: unknown) => {
    if (!value) return 'Chưa có'
    const date = new Date(String(value))
    const tz = String(timezone || 'Asia/Ho_Chi_Minh')
    if (Number.isNaN(date.getTime())) return 'Chưa có'
    try {
      const day = date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz })
      const time = date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz })
      return `${day} · ${time}`
    } catch {
      return date.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
    }
  }

  const load = useCallback(async () => {
    const db = client as any
    setLoading(true)
    const [jobsResult, runsResult] = await Promise.all([
      db.from('automation_jobs')
        .select('id,work_item_id,name,status,schedule_kind,local_time,timezone,next_run_at,last_run_at,last_status,consecutive_failures,configuration,created_at,updated_at')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true }),
      db.from('automation_runs')
        .select('id,job_id,work_item_id,status,completed_at,created_at')
        .eq('workspace_id', workspace.id)
        .gte('created_at', cutoff)
        .in('status', ['COMPLETED','FAILED'])
        .order('created_at', { ascending: false })
        .limit(500),
    ])
    if (jobsResult.error || runsResult.error) {
      setFlash({ kind: 'error', text: jobsResult.error?.message ?? runsResult.error?.message ?? 'Không thể tải đội ngũ AI.' })
      setLoading(false)
      return
    }
    const runRows = (runsResult.data ?? []) as Row[]
    setJobs((jobsResult.data ?? []) as Row[])
    setRuns(runRows)

    const completedIds = runRows.filter(row => row.status === 'COMPLETED').map(row => String(row.id)).filter(Boolean)
    if (!completedIds.length) {
      setReadIds([])
      setLoading(false)
      return
    }
    const reads = await db.from('automation_report_reads')
      .select('run_id')
      .eq('workspace_id', workspace.id)
      .eq('user_id', user.id)
      .in('run_id', completedIds)
    if (reads.error) setFlash({ kind: 'warning', text: 'Đã tải đội ngũ nhưng chưa đồng bộ trạng thái báo cáo đã đọc.' })
    else setReadIds((reads.data ?? []).map((row: Row) => String(row.run_id)))
    setLoading(false)
  }, [client, cutoff, user.id, workspace.id])

  useEffect(() => {
    void load()
    const refreshId = window.setInterval(() => void load(), 60_000)
    return () => window.clearInterval(refreshId)
  }, [load])

  const metricsByJob = useMemo(() => {
    const read = new Set(readIds)
    const map = new Map<string, AiTeamMetric>()
    for (const job of jobs) {
      const jobId = String(job.id)
      const jobRuns = runs.filter(run => String(run.job_id) === jobId)
      const completed = jobRuns.filter(run => run.status === 'COMPLETED').length
      const failed = jobRuns.filter(run => run.status === 'FAILED').length
      const denominator = completed + failed
      const unread = jobRuns.filter(run => run.status === 'COMPLETED' && !read.has(String(run.id))).length
      map.set(jobId, {
        total: denominator,
        completed,
        failed,
        successRate: denominator ? completed / denominator : null,
        unread,
      })
    }
    return map
  }, [jobs, runs, readIds])

  const openEditor = (job: Row) => {
    setEditingId(String(job.id))
    setDraftName(String(job.name ?? ''))
    setDraftTime(String(job.local_time ?? '08:00').slice(0, 5))
    setDraftTimezone(String(job.timezone ?? 'Asia/Ho_Chi_Minh'))
    setFlash(null)
  }

  async function saveJob(job: Row) {
    if (!canManage) return
    const name = draftName.trim()
    if (!name) { setFlash({ kind: 'warning', text: 'Tên nhân sự AI không được để trống.' }); return }
    if (!/^\d{2}:\d{2}$/.test(draftTime)) { setFlash({ kind: 'warning', text: 'Giờ chạy phải theo định dạng HH:MM.' }); return }

    const db = client as any
    setBusyId(String(job.id))
    setFlash(null)
    const next = await db.rpc('compute_next_daily_run', {
      p_local_time: draftTime + ':00',
      p_timezone: draftTimezone.trim(),
      p_from: new Date().toISOString(),
    })
    if (next.error) {
      setFlash({ kind: 'error', text: 'Múi giờ hoặc giờ chạy không hợp lệ: ' + next.error.message })
      setBusyId(null)
      return
    }

    const updated = await db.from('automation_jobs').update({
      name,
      local_time: draftTime + ':00',
      timezone: draftTimezone.trim(),
      next_run_at: next.data,
    }).eq('workspace_id', workspace.id).eq('id', job.id).select('*').single()

    if (updated.error) setFlash({ kind: 'error', text: updated.error.message })
    else {
      setEditingId(null)
      setFlash({ kind: 'success', text: 'Đã cập nhật nhân sự AI và tính lại lần chạy tiếp theo.' })
      await load()
    }
    setBusyId(null)
  }

  async function setJobStatus(job: Row, status: 'ACTIVE' | 'PAUSED') {
    if (!canManage) return
    const db = client as any
    setBusyId(String(job.id))
    setFlash(null)

    let patch: Record<string, unknown> = { status }
    if (status === 'ACTIVE') {
      const next = await db.rpc('compute_next_daily_run', {
        p_local_time: job.local_time,
        p_timezone: job.timezone,
        p_from: new Date().toISOString(),
      })
      if (next.error) {
        setFlash({ kind: 'error', text: next.error.message })
        setBusyId(null)
        return
      }
      patch = { ...patch, next_run_at: next.data }
    }

    const updated = await db.from('automation_jobs').update(patch)
      .eq('workspace_id', workspace.id).eq('id', job.id).select('id').single()
    if (updated.error) setFlash({ kind: 'error', text: updated.error.message })
    else {
      setFlash({ kind: status === 'ACTIVE' ? 'success' : 'warning', text: status === 'ACTIVE' ? 'Đã bật lại nhân sự AI.' : 'Đã tạm dừng nhân sự AI.' })
      await load()
    }
    setBusyId(null)
  }

  async function runNow(job: Row) {
    if (!canManage) return
    const db = client as any
    setBusyId(String(job.id))
    setFlash(null)
    const updated = await db.from('automation_jobs').update({
      status: 'ACTIVE',
      next_run_at: new Date().toISOString(),
    }).eq('workspace_id', workspace.id).eq('id', job.id).select('id').single()
    if (updated.error) setFlash({ kind: 'error', text: updated.error.message })
    else {
      setFlash({ kind: 'success', text: 'Đã yêu cầu chạy ngay. Scheduler sẽ ghi run thật khi bắt đầu xử lý.' })
      await load()
    }
    setBusyId(null)
  }

  if (loading) return <section className="page simple-work"><div className="page-heading"><div><p className="eyebrow">Đội ngũ AI</p><h1>Đang tải nhân sự AI…</h1></div><button className="secondary" onClick={onBack}>← Trang chủ</button></div></section>

  return <section className="page ai-team-manager">
    <div className="page-heading"><div><p className="eyebrow">Đội ngũ AI</p><h1>Quản lý nhân sự AI</h1><p className="muted">Tên, lịch chạy và trạng thái được quản lý tại đây. Công việc gốc và lịch sử run vẫn được giữ nguyên.</p></div>
      <button className="secondary" onClick={onBack}>← Trang chủ</button></div>
    <FlashView flash={flash} />
    {!canManage && <article className="notice warning simple-notice">Bạn có thể xem trạng thái nhưng chỉ owner workspace được thay đổi nhân sự AI.</article>}
    {!jobs.length ? <div className="empty-state"><h3>Chưa có nhân sự AI.</h3><p>Hãy giao một công việc định kỳ để iDONE tạo nhân sự AI đầu tiên.</p></div> :
      <div className="team-manager-grid">{jobs.map(job => {
        const item = items.find(candidate => candidate.id === String(job.work_item_id))
        const metric = metricsByJob.get(String(job.id)) ?? { total: 0, completed: 0, failed: 0, successRate: null, unread: 0 }
        const status = String(job.status ?? '')
        const statusLabel = status === 'ACTIVE' ? 'Đang hoạt động' : status === 'PAUSED' ? 'Đang tạm dừng' : status
        const lastLabel = job.last_status === 'COMPLETED' ? 'Thành công' : job.last_status === 'FAILED' ? 'Thất bại' : 'Chưa chạy'
        const editing = editingId === String(job.id)
        return <article className="card team-manager-card" key={String(job.id)}>
          <div className="team-manager-card-head"><div><p className="eyebrow">Nhân sự AI</p><h2>{String(job.name ?? item?.title ?? 'Nhân sự AI')}</h2></div>
            <span className={status === 'ACTIVE' ? 'runtime-badge ready' : 'runtime-badge pending'}>{statusLabel}</span></div>
          <p className="team-manager-task">{item ? displayIntentText(item.raw_intent) : 'Không tìm thấy công việc gốc.'}</p>

          <div className="team-manager-runtime">
            <div><span>Lịch</span><strong>{String(job.local_time ?? '').slice(0,5)} · {String(job.timezone ?? '')}</strong></div>
            <div><span>Tiếp theo</span><strong>{formatRuntimeTime(job.next_run_at, job.timezone)}</strong></div>
            <div><span>Gần nhất</span><strong>{job.last_run_at ? formatRuntimeTime(job.last_run_at, job.timezone) : 'Chưa chạy'}</strong></div>
            <div><span>Runtime</span><strong>{lastLabel}</strong></div>
          </div>

          <div className="team-manager-metrics">
            <div><strong>{metric.total}</strong><span>Lượt 30 ngày</span></div>
            <div><strong>{metric.completed}</strong><span>Thành công</span></div>
            <div><strong>{metric.failed}</strong><span>Thất bại</span></div>
            <div><strong>{metric.successRate === null ? '—' : Math.round(metric.successRate * 100) + '%'}</strong><span>Tỷ lệ thành công</span></div>
            <div><strong>{metric.unread}</strong><span>Báo cáo mới</span></div>
          </div>

          {editing ? <div className="team-manager-editor">
            <label>Tên nhân sự AI<input value={draftName} onChange={e => setDraftName(e.target.value)} /></label>
            <label>Giờ chạy<input type="time" value={draftTime} onChange={e => setDraftTime(e.target.value)} /></label>
            <label>Múi giờ<select value={draftTimezone} onChange={e => setDraftTimezone(e.target.value)}>
              {timezoneOptions.map(timezone => <option value={timezone} key={timezone}>{timezone}</option>)}
            </select></label>
            <p className="muted small">Tên hiển thị chỉ áp dụng cho nhân sự AI; công việc gốc và toàn bộ lịch sử vẫn được giữ nguyên.</p>
            <div className="action-row"><button className="primary" disabled={busyId === String(job.id)} onClick={() => void saveJob(job)}>Lưu</button><button className="ghost" onClick={() => setEditingId(null)}>Hủy</button></div>
          </div> : <div className="team-manager-actions">
            {canManage && <button className="secondary" onClick={() => openEditor(job)}>Chỉnh tên & lịch</button>}
            {canManage && status === 'ACTIVE' && <button className="secondary" disabled={busyId === String(job.id)} onClick={() => void setJobStatus(job, 'PAUSED')}>Tạm dừng</button>}
            {canManage && status !== 'ACTIVE' && <button className="secondary" disabled={busyId === String(job.id)} onClick={() => void setJobStatus(job, 'ACTIVE')}>Bật lại</button>}
            {canManage && <button className="secondary" disabled={busyId === String(job.id)} onClick={() => void runNow(job)}>Chạy ngay</button>}
            {item && <button className="ghost" onClick={() => onOpenWork(item)}>Mở công việc gốc</button>}
          </div>}
        </article>
      })}</div>}
  </section>
}

function WorkspaceCreate({ client, user, onDone, flash }: { client: SupabaseClient; user: User; onDone: () => Promise<void>; flash: Flash }) {
  const [name, setName] = useState('iDONE')
  const [local, setLocal] = useState<Flash>(flash)
  async function submit(event: FormEvent) {
    event.preventDefault(); setLocal(null)
    const { error } = await client.from('workspaces').insert({ name: name.trim(), owner_user_id: user.id })
    if (error) setLocal({ kind: 'error', text: error.message }); else await onDone()
  }
  return <main className="center-shell"><section className="card narrow-card"><p className="eyebrow">Thiết lập lần đầu</p><h1>Tạo workspace</h1>
    <form className="stack" onSubmit={submit}><label>Tên workspace</label><input required value={name} onChange={e => setName(e.target.value)} /><button className="primary">Tạo workspace</button></form><FlashView flash={local} /></section></main>
}

const nextActionLabel = (state: string) => ({
  DRAFT: 'iDONE đang hiểu yêu cầu',
  NEEDS_CLARIFICATION: 'Cần bạn bổ sung một ít thông tin',
  READY: 'Sẵn sàng thực hiện',
  IN_PROGRESS: 'Đang thực hiện',
  BLOCKED: 'Đang cần xử lý vướng mắc',
  READY_FOR_VERIFICATION: 'Chờ bạn kiểm tra kết quả',
  VERIFICATION_FAILED: 'Đang sửa kết quả',
  AWAITING_APPROVAL: 'Chờ bạn duyệt',
  VERIFIED_DONE: 'Đã hoàn thành',
  CANCELLED: 'Đã hủy',
}[state] ?? 'Xem công việc')

const displayIntentText = (text: string) => text.split('\n[CLARIFICATION:')[0].trim()

const cleanVisibleResult = (value: unknown) => {
  const normalize = (input: string) => input
    .replace(/\\n/g, '\n')
    .replace(/\*\*/g, '')
    .replace(/^#{1,4}\s*/gm, '')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim()

  let raw = String(value ?? '').trim()
  if (!raw) return ''

  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as { response?: unknown; choices?: Array<{ text?: unknown; message?: { content?: unknown } }> }
      const candidate = parsed.choices?.[0]?.message?.content ?? parsed.choices?.[0]?.text ?? parsed.response
      if (typeof candidate === 'string') raw = candidate
    } catch {
      // Keep plain text fallback; never expose parser errors to the user.
    }
  }

  let text = normalize(raw)
  const firstResult = text.indexOf('KẾT QUẢ')
  if (firstResult > 0) text = text.slice(firstResult)
  const repeated = text.slice(8).search(/\n---\s*\nKẾT QU/i)
  if (repeated >= 0) text = text.slice(0, repeated + 8).trim()
  return text
}

const simpleViewForState = (state: string): 'request' | 'doing' | 'result' => {
  if (['DRAFT','NEEDS_CLARIFICATION'].includes(state)) return 'request'
  if (['READY','IN_PROGRESS','BLOCKED','VERIFICATION_FAILED'].includes(state)) return 'doing'
  return 'result'
}

async function prepareIntentWork(
  client: SupabaseClient,
  user: User,
  workspace: Workspace,
  work: WorkItem,
  analysis: IntentAnalysis,
): Promise<WorkItem> {
  if (analysis.questions.length) {
    const updated = await client.from('work_items')
      .update({ lifecycle_state: 'NEEDS_CLARIFICATION' })
      .eq('workspace_id', workspace.id).eq('id', work.id)
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
    if (updated.error) throw updated.error
    const audit = await client.from('state_transitions').insert({
      workspace_id: workspace.id, work_item_id: work.id, from_state: work.lifecycle_state,
      to_state: 'NEEDS_CLARIFICATION', actor_user_id: user.id,
      reason: 'iDONE Intent Engine found material clarification gaps',
    })
    if (audit.error) throw audit.error
    return updated.data as WorkItem
  }

  const [latestSpec, latestPlan] = await Promise.all([
    client.from('work_spec_versions').select('version_no')
      .eq('workspace_id', workspace.id).eq('work_item_id', work.id)
      .order('version_no', { ascending: false }).limit(1).maybeSingle(),
    client.from('route_plan_versions').select('version_no')
      .eq('workspace_id', workspace.id).eq('work_item_id', work.id)
      .order('version_no', { ascending: false }).limit(1).maybeSingle(),
  ])
  if (latestSpec.error) throw latestSpec.error
  if (latestPlan.error) throw latestPlan.error

  const specVersion = Number(latestSpec.data?.version_no ?? 0) + 1
  const planVersion = Number(latestPlan.data?.version_no ?? 0) + 1

  const spec = await client.from('work_spec_versions').insert({
    workspace_id: workspace.id,
    work_item_id: work.id,
    version_no: specVersion,
    structured_payload: {
      schema_version: 1,
      source: 'iDONE Intent Engine v1',
      intent_analysis: analysis,
      work_spec: analysis.workSpec,
    },
    created_by: user.id,
  }).select('id').single()
  if (spec.error) throw spec.error

  const plan = await client.from('route_plan_versions').insert({
    workspace_id: workspace.id,
    work_item_id: work.id,
    version_no: planVersion,
    structured_payload: {
      steps: analysis.routePlan.steps,
      tools: analysis.routePlan.tools,
      checkpoints: analysis.routePlan.checkpoints,
      source: 'iDONE Intent Engine v1',
    },
    created_by: user.id,
  }).select('id').single()
  if (plan.error) throw plan.error

  const updated = await client.from('work_items').update({
    current_work_spec_version_id: spec.data.id,
    current_route_plan_version_id: plan.data.id,
    lifecycle_state: 'READY',
  }).eq('workspace_id', workspace.id).eq('id', work.id)
    .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
  if (updated.error) throw updated.error

  const audit = await client.from('state_transitions').insert({
    workspace_id: workspace.id, work_item_id: work.id, from_state: work.lifecycle_state,
    to_state: 'READY', actor_user_id: user.id,
    reason: 'iDONE Intent Engine produced WorkSpec + Route/Plan',
  })
  if (audit.error) throw audit.error
  return updated.data as WorkItem
}

function WorkList({ client, user, workspace, items, onCreated, onOpen, onOpenTeam }: {
  client: SupabaseClient; user: User; workspace: Workspace; items: WorkItem[]; onCreated: (w: WorkItem) => void; onOpen: (w: WorkItem) => void; onOpenTeam: () => void
}) {
  const [intent, setIntent] = useState('')
  const [flash, setFlash] = useState<Flash>(null)
  const [busy, setBusy] = useState(false)
  const [reportHistory, setReportHistory] = useState<Row[]>([])
  const [readReportIds, setReadReportIds] = useState<string[]>([])
  const [automationJobs, setAutomationJobs] = useState<Row[]>([])
  const [showReportHistory, setShowReportHistory] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const preview = useMemo(() => intent.trim().length >= 8 ? analyzeIntent(intent) : null, [intent])

  const latestReports = useMemo(() => {
    const unique: Row[] = []
    const seen = new Set<string>()
    for (const row of reportHistory) {
      const workItemId = String(row.work_item_id ?? '')
      if (!workItemId || seen.has(workItemId)) continue
      seen.add(workItemId)
      unique.push(row)
      if (unique.length >= 5) break
    }
    return unique
  }, [reportHistory])

  const unreadReportIds = useMemo(() => {
    const read = new Set(readReportIds)
    return reportHistory.filter(report => !read.has(String(report.id))).map(report => String(report.id))
  }, [reportHistory, readReportIds])

  const aiTeam = useMemo(() => automationJobs.map(job => {
    const workItemId = String(job.work_item_id ?? '')
    const item = items.find(candidate => candidate.id === workItemId)
    const status = String(job.status ?? 'DRAFT')
    const lastStatus = String(job.last_status ?? '')
    const failures = Number(job.consecutive_failures ?? 0)
    const nextRunAt = job.next_run_at ? new Date(String(job.next_run_at)) : null
    const nextRunMs = nextRunAt?.getTime() ?? Number.NaN
    const late = status === 'ACTIVE' && Number.isFinite(nextRunMs) && nextRunMs < nowMs - 5 * 60 * 1000

    let health: 'healthy' | 'paused' | 'error' | 'late' | 'setup' = 'setup'
    let healthLabel = 'Đang thiết lập'
    if (status === 'PAUSED') { health = 'paused'; healthLabel = 'Tạm dừng' }
    else if (status === 'ACTIVE' && (failures > 0 || lastStatus === 'FAILED')) { health = 'error'; healthLabel = 'Cần xử lý' }
    else if (late) { health = 'late'; healthLabel = 'Trễ lịch' }
    else if (status === 'ACTIVE') { health = 'healthy'; healthLabel = 'Hoạt động tốt' }

    const unreadCount = reportHistory.filter(report =>
      String(report.work_item_id ?? '') === workItemId && unreadReportIds.includes(String(report.id))
    ).length

    return { job, item, workItemId, health, healthLabel, unreadCount, nextRunAt, failures }
  }).filter(member => member.item), [automationJobs, items, nowMs, reportHistory, unreadReportIds])

  const aiTeamNeedsAttention = aiTeam.filter(member => member.health === 'error' || member.health === 'late').length

  useEffect(() => {
    let cancelled = false
    const db = client as any

    const loadHomeData = async () => {
      const [runsResult, jobsResult] = await Promise.all([
        db.from('automation_runs')
          .select('id,work_item_id,status,result_text,completed_at,scheduled_for,created_at')
          .eq('workspace_id', workspace.id)
          .eq('status', 'COMPLETED')
          .order('completed_at', { ascending: false })
          .limit(20),
        db.from('automation_jobs')
          .select('id,work_item_id,status,local_time,timezone,next_run_at,last_run_at,last_status,consecutive_failures,updated_at')
          .eq('workspace_id', workspace.id)
          .order('updated_at', { ascending: false }),
      ])
      if (cancelled) return

      if (runsResult.error) {
        setFlash({ kind: 'warning', text: 'Chưa tải được báo cáo tự động mới nhất.' })
        return
      }
      if (jobsResult.error) {
        setFlash({ kind: 'warning', text: 'Chưa tải được trạng thái đội ngũ AI.' })
        return
      }

      const reports = (runsResult.data ?? []) as Row[]
      setReportHistory(reports)
      setAutomationJobs((jobsResult.data ?? []) as Row[])
      setNowMs(Date.now())

      const runIds = reports.map(report => String(report.id)).filter(Boolean)
      if (!runIds.length) {
        setReadReportIds([])
        return
      }
      const reads = await db.from('automation_report_reads')
        .select('run_id')
        .eq('workspace_id', workspace.id)
        .eq('user_id', user.id)
        .in('run_id', runIds)
      if (cancelled) return
      if (reads.error) {
        setFlash({ kind: 'warning', text: 'Báo cáo đã tải nhưng chưa đồng bộ được trạng thái đã đọc.' })
        return
      }
      setReadReportIds((reads.data ?? []).map((row: Row) => String(row.run_id)))
    }

    void loadHomeData()
    const refreshId = window.setInterval(() => void loadHomeData(), 60_000)
    return () => {
      cancelled = true
      window.clearInterval(refreshId)
    }
  }, [client, user.id, workspace.id])

  async function markReportRead(report: Row) {
    const runId = String(report.id ?? '')
    if (!runId || readReportIds.includes(runId)) return
    const result = await (client as any).from('automation_report_reads').upsert({
      run_id: runId,
      workspace_id: workspace.id,
      user_id: user.id,
      read_at: new Date().toISOString(),
    }, { onConflict: 'run_id,user_id' })
    if (result.error) {
      setFlash({ kind: 'warning', text: 'Không thể lưu trạng thái đã đọc của báo cáo.' })
      return
    }
    setReadReportIds(ids => ids.includes(runId) ? ids : [...ids, runId])
  }

  async function create(event: FormEvent) {
    event.preventDefault(); setFlash(null); setBusy(true)
    const analysis = analyzeIntent(intent)
    const result = await client.from('work_items').insert({
      workspace_id: workspace.id,
      title: analysis.title,
      raw_intent: intent.trim(),
      lifecycle_state: 'DRAFT',
      created_by: user.id,
    }).select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()

    if (result.error) {
      setFlash({ kind: 'error', text: result.error.message }); setBusy(false); return
    }
    try {
      const prepared = await prepareIntentWork(client, user, workspace, result.data as WorkItem, analysis)
      onCreated(prepared)
      setIntent('')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không thể chuẩn bị công việc.'
      setFlash({ kind: 'error', text: message })
    } finally {
      setBusy(false)
    }
  }

  return <section className="page simple-home">
    <div className="hero-copy"><p className="eyebrow">iDONE · Customer Zero</p><h1>Bạn muốn iDONE hoàn thành việc gì?</h1>
      <p className="hero-subtitle">Chỉ cần mô tả kết quả bạn muốn. WorkSpec, kế hoạch, kiểm chứng và nhật ký được iDONE xử lý ở phía sau.</p></div>
    <form className="intent-composer" onSubmit={create}>
      <textarea rows={6} required autoFocus value={intent} onChange={e => setIntent(e.target.value)}
        placeholder="Ví dụ: Nghiên cứu các sự kiện khởi nghiệp tại Cà Mau trong 3 tháng tới, phân tích SWOT và tạo bảng tổng hợp có thể sử dụng ngay." />
      {preview && <div className="intent-preview"><span>iDONE hiểu đầu ra:</span><strong>{preview.expectedOutput}</strong><span>{preview.questions.length ? `Cần xác nhận ${preview.questions.length} điểm` : 'Đã đủ thông tin để bắt đầu'}</span></div>}
      <button className="primary intent-submit" disabled={busy || intent.trim().length < 4}>{busy ? 'iDONE đang hiểu yêu cầu…' : 'Giao việc cho iDONE'}</button>
      <FlashView flash={flash} />
    </form>
    {aiTeam.length > 0 && <section className="ai-team-health">
      <div className="home-section-heading ai-team-heading">
        <div><p className="eyebrow">Đội ngũ AI</p><h2>Sức khỏe nhân sự AI</h2><p className="muted report-center-subtitle">Theo dõi ai đang chạy tốt, ai cần chú ý và lịch làm việc tiếp theo.</p></div>
        <div className="ai-team-summary"><span>{aiTeam.length} nhân sự</span>{aiTeamNeedsAttention > 0 ? <span className="attention-count">{aiTeamNeedsAttention} cần chú ý</span> : <span className="healthy-count">Tất cả ổn định</span>}<button type="button" className="ghost" onClick={onOpenTeam}>Quản lý đội ngũ</button></div>
      </div>
      <div className="ai-team-grid">{aiTeam.map(member => {
        const job = member.job
        const item = member.item!
        const localTime = String(job.local_time ?? '').slice(0, 5) || '—'
        const lastRun = job.last_run_at ? new Date(String(job.last_run_at)).toLocaleString('vi-VN') : 'Chưa chạy'
        const nextRun = member.nextRunAt ? member.nextRunAt.toLocaleString('vi-VN') : 'Chưa có'
        const alertText = member.health === 'error'
          ? `Lần chạy gần nhất lỗi${member.failures > 1 ? ` · ${member.failures} lỗi liên tiếp` : ''}.`
          : member.health === 'late'
            ? 'Đã quá thời điểm chạy dự kiến hơn 5 phút.'
            : null
        return <button className={`ai-member-card ${member.health}`} key={String(job.id)} onClick={() => onOpen(item)}>
          <div className="ai-member-top"><span className={`health-dot ${member.health}`}>●</span><span className={`health-label ${member.health}`}>{member.healthLabel}</span>{member.unreadCount > 0 && <span className="new-report-badge">{member.unreadCount} mới</span>}</div>
          <h3>{item.title}</h3>
          <div className="ai-member-metrics">
            <div><span>Lịch</span><strong>{localTime} · {String(job.timezone ?? '')}</strong></div>
            <div><span>Tiếp theo</span><strong>{nextRun}</strong></div>
            <div><span>Gần nhất</span><strong>{lastRun}</strong></div>
          </div>
          {alertText && <p className="ai-health-alert">{alertText}</p>}
          <span className="ai-member-open">Mở nhân sự AI →</span>
        </button>
      })}</div>
    </section>}

    {latestReports.length > 0 && <section className="report-center">
      <div className="home-section-heading report-center-heading">
        <div><p className="eyebrow">Nhân sự AI</p><h2>Hộp thư báo cáo</h2><p className="muted report-center-subtitle">Mở iDONE là thấy ngay những gì đội ngũ AI vừa hoàn thành.</p></div>
        <div className="report-center-meta">{unreadReportIds.length > 0 && <span className="unread-count">{unreadReportIds.length} mới</span>}<button className="ghost" type="button" onClick={() => setShowReportHistory(value => !value)}>{showReportHistory ? 'Ẩn lịch sử' : 'Lịch sử báo cáo'}</button></div>
      </div>
      <div className="report-center-grid">{latestReports.map(report => {
        const item = items.find(candidate => candidate.id === String(report.work_item_id))
        if (!item) return null
        const reportId = String(report.id)
        const unread = unreadReportIds.includes(reportId)
        const body = cleanVisibleResult(report.result_text)
        const previewText = body.length > 220 ? body.slice(0, 220).trim() + '…' : body
        return <button className={unread ? 'report-card unread' : 'report-card'} key={reportId} onClick={() => { void markReportRead(report); onOpen(item) }}>
          <div className="report-card-top"><span className={unread ? 'report-dot unread' : 'report-dot'}>●</span><span>{new Date(String(report.completed_at ?? report.created_at)).toLocaleString('vi-VN')}</span>{unread && <span className="new-report-badge">Mới</span>}</div>
          <h3>{item.title}</h3>
          <p>{previewText || 'Báo cáo đã hoàn thành.'}</p>
          <strong>Xem báo cáo →</strong>
        </button>
      })}</div>

      {showReportHistory && <div className="report-history">
        <div className="report-history-heading"><h3>Lịch sử gần đây</h3><span>{reportHistory.length} lượt chạy</span></div>
        <div className="report-history-list">{reportHistory.map(report => {
          const item = items.find(candidate => candidate.id === String(report.work_item_id))
          if (!item) return null
          const reportId = String(report.id)
          const unread = unreadReportIds.includes(reportId)
          return <details className={unread ? 'report-history-item unread' : 'report-history-item'} key={reportId}
            onToggle={event => { if ((event.currentTarget as HTMLDetailsElement).open) void markReportRead(report) }}>
            <summary><div><strong>{item.title}</strong><span>{new Date(String(report.completed_at ?? report.created_at)).toLocaleString('vi-VN')}</span></div>{unread && <span className="new-report-badge">Mới</span>}</summary>
            <div className="report-history-body"><div className="result-output">{cleanVisibleResult(report.result_text)}</div><button type="button" className="secondary" onClick={() => onOpen(item)}>Mở công việc</button></div>
          </details>
        })}</div>
      </div>}
    </section>}
    <div className="home-section-heading"><h2>Công việc gần đây</h2><span>{items.length} công việc</span></div>
    {!items.length ? <div className="empty-state"><h3>Chưa có công việc.</h3><p>Giao một việc ở ô phía trên để bắt đầu.</p></div> : null}
    <div className="work-list">{items.map(item => <button className="work-row simple" key={item.id} onClick={() => onOpen(item)}>
      <div><h3>{item.title}</h3><p>{displayIntentText(item.raw_intent)}</p><span className="next-action">{nextActionLabel(item.lifecycle_state)}</span></div>
      <div className="row-meta"><Badge value={item.lifecycle_state} /><span>{new Date(item.updated_at).toLocaleString('vi-VN')}</span></div>
    </button>)}</div>
  </section>
}

function WorkFlow({ client, user, workspace, initial, onBack }: { client: SupabaseClient; user: User; workspace: Workspace; initial: WorkItem; onBack: () => void }) {
  const [work, setWork] = useState(initial)
  const [tab, setTab] = useState<'spec'|'plan'|'execute'|'verify'|'done'|'history'>('spec')
  const [simpleView, setSimpleView] = useState<'request'|'doing'|'result'>(() => simpleViewForState(initial.lifecycle_state))
  const [customClarification, setCustomClarification] = useState('')
  const [showCustomClarification, setShowCustomClarification] = useState(false)
  const [simpleResult, setSimpleResult] = useState('')
  const [simpleArtifact, setSimpleArtifact] = useState('')
  const [executorRunning, setExecutorRunning] = useState(false)
  const [executorError, setExecutorError] = useState('')
  const [factualChecked, setFactualChecked] = useState(false)
  const [acceptanceChecked, setAcceptanceChecked] = useState(false)
  const [records, setRecords] = useState<{ specs: Row[]; plans: Row[]; executions: Row[]; artifacts: Row[]; evidence: Row[]; verifications: Row[]; defects: Row[]; approvals: Row[]; transitions: Row[]; decisions: Row[]; automationJobs: Row[]; automationRuns: Row[] }>({
    specs: [], plans: [], executions: [], artifacts: [], evidence: [], verifications: [], defects: [], approvals: [], transitions: [], decisions: [], automationJobs: [], automationRuns: [],
  })
  const [flash, setFlash] = useState<Flash>(null)
  const load = useCallback(async () => {
    const db = client as any
    const tables = ['work_spec_versions','route_plan_versions','execution_records','artifact_references','evidence_records','verification_records','defects','approval_records','state_transitions','decision_links','automation_jobs','automation_runs'] as const
    const results = await Promise.all(tables.map(table => db.from(table).select('*').eq('workspace_id', workspace.id).eq('work_item_id', work.id).order('created_at', { ascending: false })))
    const failed = results.find(r => r.error)
    if (failed?.error) setFlash({ kind: 'error', text: failed.error.message })
    setRecords({ specs: results[0].data ?? [], plans: results[1].data ?? [], executions: results[2].data ?? [], artifacts: results[3].data ?? [], evidence: results[4].data ?? [], verifications: results[5].data ?? [], defects: results[6].data ?? [], approvals: results[7].data ?? [], transitions: results[8].data ?? [], decisions: results[9].data ?? [], automationJobs: results[10].data ?? [], automationRuns: results[11].data ?? [] })
  }, [client, workspace.id, work.id])
  useEffect(() => { void load() }, [load])

  async function transition(to: string, reason: string) {
    const currentResult = await client.from('work_items')
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at')
      .eq('workspace_id', workspace.id).eq('id', work.id).single()
    if (currentResult.error) { setFlash({ kind: 'error', text: currentResult.error.message }); return false }
    const current = currentResult.data as WorkItem
    if (current.lifecycle_state !== to && !canTransition(current.lifecycle_state, to)) {
      setFlash({ kind: 'error', text: `Transition không hợp lệ: ${current.lifecycle_state} → ${to}.` }); return false
    }
    const from = current.lifecycle_state
    const updated = await client.from('work_items').update({ lifecycle_state: to }).eq('workspace_id', workspace.id).eq('id', work.id)
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
    if (updated.error) { setFlash({ kind: 'error', text: updated.error.message }); return false }
    if (from !== to) {
      const audit = await client.from('state_transitions').insert({ workspace_id: workspace.id, work_item_id: work.id, from_state: from, to_state: to, actor_user_id: user.id, reason })
      if (audit.error) {
        await client.from('work_items').update({ lifecycle_state: from }).eq('workspace_id', workspace.id).eq('id', work.id)
        setFlash({ kind: 'error', text: `Audit thất bại; state đã rollback. ${audit.error.message}` }); return false
      }
    }
    setWork(updated.data as WorkItem); return true
  }

  const gate = useMemo(() => {
    const latestVerifications = records.verifications.filter(
      (row, index, all) => all.findIndex(candidate => candidate.criterion_ref === row.criterion_ref) === index,
    )
    return evaluateDoneGate({
      hasWorkSpec: Boolean(work.current_work_spec_version_id), hasPlan: Boolean(work.current_route_plan_version_id),
      hasCompletedExecution: records.executions.some(r => r.status === 'COMPLETED'), evidenceCount: records.evidence.length,
      verificationResults: latestVerifications.map(r => String(r.result)) as Array<'PASS'|'FAIL'|'NOT CHECKABLE'|'N/A'>,
      defects: records.defects.map(r => ({ severity: String(r.severity) as 'Blocker'|'Critical'|'Major'|'Minor', state: String(r.state) as 'OPEN'|'RESOLVED'|'WAIVED' })),
      factualIntegrityConfirmed: latestVerifications.some(r => r.criterion_ref === 'D-032' && r.result === 'PASS'),
      acceptanceVerified: latestVerifications.some(r => r.criterion_ref !== 'D-032' && r.result === 'PASS'),
      approvalGranted: records.approvals.some(r => r.decision === 'APPROVED'),
    })
  }, [records, work])

  const analysis = useMemo(() => analyzeIntent(work.raw_intent), [work.raw_intent])
  const latestExecution = records.executions[0]
  const latestArtifact = records.artifacts[0]
  const planSteps = useMemo(() => {
    const payload = records.plans[0]?.structured_payload
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const steps = (payload as Record<string, unknown>).steps
      if (Array.isArray(steps)) return steps.map(String)
    }
    return analysis.routePlan.steps
  }, [records.plans, analysis.routePlan.steps])

  useEffect(() => {
    setSimpleView(simpleViewForState(work.lifecycle_state))
  }, [work.lifecycle_state])

  const clarificationAnsweredCount = useMemo(
    () => (work.raw_intent.match(/\[CLARIFICATION:/g) ?? []).length,
    [work.raw_intent],
  )
  const currentQuestion = analysis.questions[0]
  const clarificationTotal = Math.min(3, clarificationAnsweredCount + analysis.questions.length)
  const showAudit = useMemo(() => new URLSearchParams(window.location.search).get('audit') === '1', [])

  async function persistClarification(questionId: string, answer: string, source: 'user' | 'suggestion' | 'auto') {
    const cleanAnswer = answer.trim()
    if (!cleanAnswer) return
    const nextIntent = `${work.raw_intent}\n[CLARIFICATION:${questionId}] [${source}] ${cleanAnswer}`
    const nextAnalysis = analyzeIntent(nextIntent)
    const answeredAfter = (nextIntent.match(/\[CLARIFICATION:/g) ?? []).length
    const finalAnalysis: IntentAnalysis = answeredAfter >= 3 ? { ...nextAnalysis, questions: [] } : nextAnalysis

    const updated = await client.from('work_items').update({
      raw_intent: nextIntent,
    }).eq('workspace_id', workspace.id).eq('id', work.id)
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
    if (updated.error) { setFlash({ kind: 'error', text: updated.error.message }); return }

    setCustomClarification('')
    setShowCustomClarification(false)

    if (finalAnalysis.questions.length > 0) {
      setWork(updated.data as WorkItem)
      setFlash(null)
      return
    }

    try {
      const prepared = await prepareIntentWork(client, user, workspace, updated.data as WorkItem, finalAnalysis)
      setWork(prepared)
      await load()
      const schedule = finalAnalysis.scheduleHint ? ` · ${finalAnalysis.scheduleHint}` : ''
      setFlash({ kind: 'success', text: `Đã rõ. iDONE sẽ tạo: ${finalAnalysis.expectedOutput}${schedule}.` })
    } catch (error) {
      setFlash({ kind: 'error', text: error instanceof Error ? error.message : 'Không thể chuẩn bị công việc.' })
    }
  }

  async function submitCustomClarification(event: FormEvent) {
    event.preventDefault()
    if (!currentQuestion) return
    await persistClarification(currentQuestion.id, customClarification, 'user')
  }

  async function acceptAllSuggestions() {
    if (!analysis.questions.length) return
    const selected = analysis.questions.slice(0, Math.max(1, 3 - clarificationAnsweredCount))
    let nextIntent = work.raw_intent
    for (const question of selected) {
      nextIntent += `\n[CLARIFICATION:${question.id}] [auto] ${question.suggestion}`
    }
    const nextAnalysis = analyzeIntent(nextIntent)
    const resolvedAnalysis: IntentAnalysis = { ...nextAnalysis, questions: [] }

    const updated = await client.from('work_items').update({ raw_intent: nextIntent })
      .eq('workspace_id', workspace.id).eq('id', work.id)
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
    if (updated.error) { setFlash({ kind: 'error', text: updated.error.message }); return }

    try {
      const prepared = await prepareIntentWork(client, user, workspace, updated.data as WorkItem, resolvedAnalysis)
      setWork(prepared)
      setCustomClarification('')
      setShowCustomClarification(false)
      await load()
      const schedule = resolvedAnalysis.scheduleHint ? ` · ${resolvedAnalysis.scheduleHint}` : ''
      setFlash({ kind: 'success', text: `Đã dùng đề xuất của iDONE. Bắt đầu với: ${resolvedAnalysis.expectedOutput}${schedule}.` })
    } catch (error) {
      setFlash({ kind: 'error', text: error instanceof Error ? error.message : 'Không thể chuẩn bị công việc.' })
    }
  }

  async function ensureAutomationJob() {
    if (!analysis.isRecurring) return null
    const existing = records.automationJobs[0]
    if (existing?.id && existing.status === 'ACTIVE') return existing

    const scheduleMatch = analysis.scheduleHint?.match(/(\d{2}):(\d{2})/)
    const localTime = scheduleMatch ? `${scheduleMatch[1]}:${scheduleMatch[2]}:00` : '08:00:00'
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Ho_Chi_Minh'
    const db = client as any
    const next = await db.rpc('compute_next_daily_run', {
      p_local_time: localTime,
      p_timezone: timezone,
      p_from: new Date().toISOString(),
    })
    if (next.error) throw next.error

    const payload = {
      workspace_id: workspace.id,
      work_item_id: work.id,
      owner_user_id: user.id,
      name: work.title,
      status: 'ACTIVE',
      schedule_kind: 'DAILY',
      local_time: localTime,
      timezone,
      next_run_at: next.data,
      configuration: {
        expected_output: analysis.expectedOutput,
        sources: [
          { name: 'Hacker News', type: 'hn', url: 'https://hn.algolia.com/api/v1/search_by_date?tags=story&hitsPerPage=15' },
          { name: 'TechCrunch', type: 'rss', url: 'https://techcrunch.com/feed/' },
          { name: 'The Verge', type: 'rss', url: 'https://www.theverge.com/rss/index.xml' },
        ],
      },
    }
    const saved = await db.from('automation_jobs').upsert(payload, { onConflict: 'work_item_id' }).select('*').single()
    if (saved.error) throw saved.error
    return saved.data as Row
  }

  async function runAutonomousExecutor() {
    if (executorRunning) return
    if (!work.current_route_plan_version_id) {
      setExecutorError('iDONE chưa có kế hoạch thực thi.')
      return
    }
    setExecutorRunning(true)
    setExecutorError('')
    setFlash(null)

    const previousState = work.lifecycle_state
    if (['READY','VERIFICATION_FAILED','BLOCKED'].includes(previousState)) {
      const started = await transition('IN_PROGRESS', 'iDONE autonomous executor started')
      if (!started) { setExecutorRunning(false); return }
    }

    let executorAuditDetail = ''
    try {
      const automationJob = analysis.isRecurring ? await ensureAutomationJob() : null
      const { data: sessionData } = await client.auth.getSession()
      const accessToken = sessionData.session?.access_token
      if (!accessToken) throw new Error('Phiên đăng nhập đã hết hạn.')

      const response = await fetch('/api/execute', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          intent: work.raw_intent,
          kind: analysis.kind,
          audience: analysis.audience,
          expectedOutput: analysis.expectedOutput,
          needsFreshInfo: analysis.needsFreshInfo,
          needsExternalAction: analysis.needsExternalAction,
          isRecurring: analysis.isRecurring,
          scheduleHint: analysis.scheduleHint,
          schedulerActive: Boolean(automationJob),
          nextRunAt: automationJob?.next_run_at ?? null,
        }),
      })
      const payload = await response.json() as { result?: string; error?: string; detail?: string; model?: string }
      executorAuditDetail = payload.detail ?? payload.error ?? `HTTP ${response.status}`
      if (!response.ok || !payload.result) {
        throw new Error(payload.error || 'iDONE AI tạm thời chưa thể xử lý lượt này.')
      }

      const execution = await client.from('execution_records').insert({
        workspace_id: workspace.id,
        work_item_id: work.id,
        route_plan_version_id: work.current_route_plan_version_id,
        execution_type: 'WORK',
        actor_user_id: user.id,
        capability: 'WORKERS_AI',
        status: 'COMPLETED',
        started_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        notes: payload.result,
      }).select('id').single()
      if (execution.error) throw execution.error

      if (previousState === 'VERIFICATION_FAILED') {
        const resolved = await client.from('defects').update({
          state: 'RESOLVED',
          resolution_ref: `Remediated by AI execution ${String(execution.data.id)}`,
        }).eq('workspace_id', workspace.id).eq('work_item_id', work.id).eq('state', 'OPEN').eq('severity', 'Major')
        if (resolved.error) throw resolved.error
      }

      const ready = await transition('READY_FOR_VERIFICATION', 'iDONE autonomous executor produced result')
      if (!ready) throw new Error('Không thể chuyển kết quả sang bước kiểm tra.')

      await load()
      setSimpleView('result')
      setFlash({ kind: 'success', text: analysis.isRecurring ? 'Nhân sự AI đã được kích hoạt. Hãy kiểm tra kết quả và lịch chạy.' : 'iDONE đã hoàn thành lượt xử lý. Hãy xem kết quả.' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'iDONE AI chưa thể thực hiện.'
      setExecutorError(message)
      const auditReason = executorAuditDetail
        ? `Autonomous executor unavailable: ${executorAuditDetail.slice(0, 300)}`
        : 'Autonomous executor unavailable'
      await transition('BLOCKED', auditReason)
      await load()
    } finally {
      setExecutorRunning(false)
    }
  }

  useEffect(() => {
    if (work.lifecycle_state === 'READY' && records.executions.length === 0 && !executorRunning) {
      void runAutonomousExecutor()
    }
  }, [work.lifecycle_state, records.executions.length, executorRunning])

  async function recordSimpleResult(event: FormEvent) {
    event.preventDefault()
    const output = simpleResult.trim()
    if (!output) return
    if (!work.current_route_plan_version_id) { setFlash({ kind: 'warning', text: 'iDONE chưa có kế hoạch thực thi.' }); return }

    if (['READY','VERIFICATION_FAILED','BLOCKED'].includes(work.lifecycle_state)) {
      const started = await transition('IN_PROGRESS', 'Simple surface execution started')
      if (!started) return
    }

    const remediating = work.lifecycle_state === 'VERIFICATION_FAILED'
    const result = await client.from('execution_records').insert({
      workspace_id: workspace.id,
      work_item_id: work.id,
      route_plan_version_id: work.current_route_plan_version_id,
      execution_type: 'WORK',
      actor_user_id: user.id,
      capability: 'AI_EXECUTOR_BRIDGE',
      status: 'COMPLETED',
      started_at: new Date().toISOString(),
      completed_at: new Date().toISOString(),
      notes: output,
    }).select('id').single()
    if (result.error) { setFlash({ kind: 'error', text: result.error.message }); return }

    if (remediating) {
      const resolved = await client.from('defects').update({
        state: 'RESOLVED',
        resolution_ref: `Remediated by execution ${String(result.data.id)}`,
      }).eq('workspace_id', workspace.id).eq('work_item_id', work.id).eq('state', 'OPEN').eq('severity', 'Major')
      if (resolved.error) { setFlash({ kind: 'error', text: resolved.error.message }); return }
    }

    if (simpleArtifact.trim()) {
      const artifact = await client.from('artifact_references').insert({
        workspace_id: workspace.id,
        work_item_id: work.id,
        execution_record_id: result.data.id,
        url_ref: simpleArtifact.trim(),
        metadata: { label: 'Kết quả công việc' },
        created_by: user.id,
      })
      if (artifact.error) { setFlash({ kind: 'error', text: artifact.error.message }); return }
    }

    const ready = await transition('READY_FOR_VERIFICATION', 'Result recorded from AI executor bridge')
    if (!ready) return
    setSimpleResult(''); setSimpleArtifact('')
    await load()
    setSimpleView('result')
    setFlash({ kind: 'success', text: 'Đã nhận kết quả. Bây giờ chỉ cần kiểm tra và duyệt.' })
  }

  async function createSimpleEvidence() {
    if (!latestExecution?.id) throw new Error('Chưa có kết quả để kiểm chứng.')
    const evidence = await client.from('evidence_records').insert({
      workspace_id: workspace.id,
      work_item_id: work.id,
      evidence_type: 'DỮ KIỆN',
      provenance_status: 'SYSTEM-GENERATED',
      source_locator: latestArtifact?.url_ref ? String(latestArtifact.url_ref) : `execution:${String(latestExecution.id)}`,
      reference_payload: { text: String(latestExecution.notes ?? '') },
      uncertainty_status: 'KNOWN',
      producer_user_id: user.id,
    }).select('id').single()
    if (evidence.error) throw evidence.error
    return String(evidence.data.id)
  }

  async function verifySimple() {
    const activeAutomation = records.automationJobs[0]
    const completedAutomationRun = records.automationRuns.find(row => row.status === 'COMPLETED')
    if (analysis.isRecurring && activeAutomation?.status !== 'ACTIVE') {
      setFlash({ kind: 'warning', text: 'Chưa thể duyệt: nhân sự AI chưa được kích hoạt.' })
      return
    }
    if (analysis.isRecurring && !completedAutomationRun) {
      setFlash({ kind: 'warning', text: 'Chưa thể duyệt: nhân sự AI cần ít nhất một lần chạy thử thành công.' })
      return
    }
    if (analysis.isRecurring && (!activeAutomation?.next_run_at || activeAutomation?.last_status !== 'COMPLETED')) {
      setFlash({ kind: 'warning', text: 'Chưa thể duyệt: lịch chạy hoặc trạng thái runtime chưa đủ bằng chứng.' })
      return
    }
    if (!factualChecked || !acceptanceChecked) {
      setFlash({ kind: 'warning', text: 'Hãy xác nhận cả tính đúng của thông tin và mức đáp ứng yêu cầu.' })
      return
    }
    try {
      const evidenceId = await createSimpleEvidence()
      const factual = await client.from('verification_records').insert({
        workspace_id: workspace.id, work_item_id: work.id, criterion_ref: 'D-032', result: 'PASS',
        evidence_refs: [evidenceId], verifier_user_id: user.id, verifier_capability: 'FOUNDER_REVIEW',
        comments: 'Founder xác nhận thông tin thực tế đã được kiểm tra trên simple surface.',
      })
      if (factual.error) throw factual.error
      const acceptance = await client.from('verification_records').insert({
        workspace_id: workspace.id, work_item_id: work.id, criterion_ref: 'DoD', result: 'PASS',
        evidence_refs: [evidenceId], verifier_user_id: user.id, verifier_capability: 'FOUNDER_REVIEW',
        comments: 'Founder xác nhận kết quả đáp ứng yêu cầu/tiêu chí nghiệm thu.',
      })
      if (acceptance.error) throw acceptance.error
      const ok = await transition('AWAITING_APPROVAL', 'Founder review passed factual integrity + acceptance')
      if (!ok) return
      await load()
      setFlash({ kind: 'success', text: 'Kiểm tra đã đạt. iDONE đang chờ quyết định hoàn thành cuối cùng.' })
    } catch (error) {
      setFlash({ kind: 'error', text: error instanceof Error ? error.message : 'Không thể ghi verification.' })
    }
  }

  async function requestSimpleFix() {
    const reason = window.prompt('Điểm nào cần sửa?')
    if (!reason?.trim()) return
    try {
      const evidenceId = await createSimpleEvidence()
      const verification = await client.from('verification_records').insert({
        workspace_id: workspace.id, work_item_id: work.id, criterion_ref: 'DoD', result: 'FAIL',
        evidence_refs: [evidenceId], verifier_user_id: user.id, verifier_capability: 'FOUNDER_REVIEW',
        comments: reason.trim(),
      }).select('id').single()
      if (verification.error) throw verification.error
      const defect = await client.from('defects').insert({
        workspace_id: workspace.id, work_item_id: work.id, verification_record_id: verification.data.id,
        severity: 'Major', state: 'OPEN', description: reason.trim(),
      })
      if (defect.error) throw defect.error
      const ok = await transition('VERIFICATION_FAILED', 'Founder requested remediation')
      if (!ok) return
      await load()
      setSimpleView('doing')
      setFlash({ kind: 'warning', text: 'Đã ghi yêu cầu sửa. Công việc quay lại bước thực hiện.' })
    } catch (error) {
      setFlash({ kind: 'error', text: error instanceof Error ? error.message : 'Không thể ghi yêu cầu sửa.' })
    }
  }

  async function approveSimple() {
    const blockers = gate.missing.filter(x => x !== 'Thiếu approval rõ ràng.')
    if (blockers.length) { setFlash({ kind: 'warning', text: 'Chưa thể hoàn thành: ' + blockers.join(' ') }); return }
    const approval = await client.from('approval_records').insert({
      workspace_id: workspace.id, work_item_id: work.id, target_type: 'WORK_ITEM', target_id: work.id,
      approver_user_id: user.id, decision: 'APPROVED', scope: 'Simple surface final completion',
      source_locator: 'Founder action in iDONE simple surface',
    })
    if (approval.error) { setFlash({ kind: 'error', text: approval.error.message }); return }
    const ok = await transition('VERIFIED_DONE', 'Founder approval + DONE gate PASS')
    if (!ok) return
    await load()
    setSimpleView('result')
    setFlash({ kind: 'success', text: 'Đã hoàn thành và được kiểm chứng.' })
  }

  async function rejectSimple() {
    const reason = window.prompt('Lý do chưa duyệt:')
    if (!reason?.trim()) return
    const approval = await client.from('approval_records').insert({
      workspace_id: workspace.id, work_item_id: work.id, target_type: 'WORK_ITEM', target_id: work.id,
      approver_user_id: user.id, decision: 'NOT APPROVED', scope: reason.trim(),
      source_locator: 'Founder action in iDONE simple surface',
    })
    if (approval.error) { setFlash({ kind: 'error', text: approval.error.message }); return }
    const ok = await transition('VERIFICATION_FAILED', 'Founder rejected result')
    if (!ok) return
    await load()
    setSimpleView('doing')
    setFlash({ kind: 'warning', text: 'Đã yêu cầu sửa. Công việc quay lại bước thực hiện.' })
  }

  async function exportSimple() {
    const payload = buildPortableExport({
      work_item: work, work_specs: records.specs, route_plans: records.plans, executions: records.executions,
      artifacts: records.artifacts, evidence: records.evidence, verifications: records.verifications,
      defects: records.defects, approvals: records.approvals, transitions: records.transitions,
      decisions: records.decisions, done_gate: gate,
    })
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `idone-${work.id}.json`
    anchor.click()
    URL.revokeObjectURL(url)
    const logged = await client.from('export_packages').insert({
      workspace_id: workspace.id, work_item_id: work.id, format: 'JSON', created_by: user.id,
    })
    if (logged.error) setFlash({ kind: 'warning', text: 'Đã tải file nhưng chưa ghi được export log: ' + logged.error.message })
    else setFlash({ kind: 'success', text: 'Đã xuất gói kết quả và lịch sử kiểm chứng.' })
  }

  async function cancelWork() {
    if (['VERIFIED_DONE','CANCELLED'].includes(work.lifecycle_state)) return
    const reason = window.prompt('Lý do hủy công việc:')
    if (!reason?.trim()) return
    const saved = await client.from('work_items').update({ cancelled_reason: reason.trim() }).eq('workspace_id', workspace.id).eq('id', work.id)
    if (saved.error) { setFlash({ kind: 'error', text: saved.error.message }); return }
    const ok = await transition('CANCELLED', reason.trim())
    if (ok) { await load(); setFlash({ kind: 'warning', text: 'Công việc đã CANCELLED và lưu lý do trong audit trail.' }) }
  }

  const tabs = [['spec','WorkSpec'],['plan','Kế hoạch'],['execute','Thực thi'],['verify','Kiểm chứng'],['done','Duyệt & DONE'],['history','Lịch sử & Xuất']] as const
  const simpleTabs = [['request','1. Yêu cầu'],['doing','2. Đang làm'],['result','3. Kết quả']] as const
  const automationJob = records.automationJobs[0]
  const latestAutomationRun = records.automationRuns.find(row => row.status === 'COMPLETED') ?? records.automationRuns[0]
  const automationRuntimeReady = Boolean(
    analysis.isRecurring &&
    automationJob?.status === 'ACTIVE' &&
    automationJob?.next_run_at &&
    automationJob?.last_status === 'COMPLETED' &&
    latestAutomationRun?.status === 'COMPLETED'
  )
  const resultText = analysis.isRecurring && latestAutomationRun?.result_text
    ? cleanVisibleResult(latestAutomationRun.result_text)
    : cleanVisibleResult(latestExecution?.notes)

  const formatRuntimeDate = (value: unknown) => value
    ? new Date(String(value)).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
    : 'Chưa có'

  async function pauseAutomation() {
    if (!automationJob?.id) return
    const updated = await (client as any).from('automation_jobs').update({ status: 'PAUSED' })
      .eq('workspace_id', workspace.id).eq('id', automationJob.id).select('*').single()
    if (updated.error) { setFlash({ kind: 'error', text: updated.error.message }); return }
    await load()
    setFlash({ kind: 'warning', text: 'Đã tạm dừng nhân sự AI. Lịch chạy sẽ không thực hiện cho đến khi bạn bật lại.' })
  }

  async function resumeAutomation() {
    if (!automationJob?.id) return
    const next = await (client as any).rpc('compute_next_daily_run', {
      p_local_time: automationJob.local_time,
      p_timezone: automationJob.timezone,
      p_from: new Date().toISOString(),
    })
    if (next.error) { setFlash({ kind: 'error', text: next.error.message }); return }
    const updated = await (client as any).from('automation_jobs').update({
      status: 'ACTIVE',
      next_run_at: next.data,
    }).eq('workspace_id', workspace.id).eq('id', automationJob.id).select('*').single()
    if (updated.error) { setFlash({ kind: 'error', text: updated.error.message }); return }
    await load()
    setFlash({ kind: 'success', text: 'Đã bật lại nhân sự AI và cập nhật lần chạy tiếp theo.' })
  }

  async function requestAutomationRunNow() {
    if (!automationJob?.id) return
    const updated = await (client as any).from('automation_jobs').update({
      status: 'ACTIVE',
      next_run_at: new Date().toISOString(),
    }).eq('workspace_id', workspace.id).eq('id', automationJob.id).select('*').single()
    if (updated.error) { setFlash({ kind: 'error', text: updated.error.message }); return }
    await load()
    setFlash({ kind: 'success', text: 'Đã yêu cầu chạy thử ngay. Scheduler sẽ nhận job trong vòng khoảng 1 phút.' })
  }

  return <section className="page simple-work">
    <div className="page-heading"><div><p className="eyebrow">iDONE Brain · {work.id.slice(0, 8)}</p><h1>{work.title}</h1><p className="muted">{work.lifecycle_state === 'NEEDS_CLARIFICATION' ? `Cần xác nhận ${analysis.questions.length} điểm` : nextActionLabel(work.lifecycle_state)}</p></div>
      <div className="header-actions"><button className="secondary" onClick={onBack}>← Công việc</button>{!['VERIFIED_DONE','CANCELLED'].includes(work.lifecycle_state) && <button className="ghost danger-text" onClick={() => void cancelWork()}>Hủy</button>}</div></div>

    <div className="simple-stage-tabs">{simpleTabs.map(([id,label]) => <button key={id} className={simpleView === id ? 'active' : ''} onClick={() => setSimpleView(id)}>{label}</button>)}</div>
    <FlashView flash={flash} />

    {simpleView === 'request' && <div className="simple-stage">
      <article className="card intent-understanding">
        <p className="eyebrow">Bạn yêu cầu</p><h2 className="request-text">{displayIntentText(work.raw_intent)}</h2>
        <div className="understanding-grid">
          <div><span>iDONE hiểu đầu ra</span><strong>{analysis.expectedOutput}</strong></div>
          <div><span>{analysis.isRecurring ? 'Lịch' : 'Đối tượng'}</span><strong>{analysis.isRecurring ? (analysis.scheduleHint ?? 'Định kỳ') : analysis.audience}</strong></div>
          <div><span>Tình trạng</span><strong>{analysis.questions.length ? `Còn ${analysis.questions.length} điểm cần xác nhận` : 'Đã đủ thông tin'}</strong></div>
        </div>
      </article>
      {work.lifecycle_state === 'NEEDS_CLARIFICATION' && currentQuestion ? <section className="card clarification-focus">
        <div className="clarification-topline"><div><p className="eyebrow">Xác nhận nhanh</p><span className="clarification-progress">Câu {Math.min(clarificationAnsweredCount + 1, clarificationTotal)} / {clarificationTotal}</span></div>
          <button type="button" className="ghost auto-decide" onClick={() => void acceptAllSuggestions()}>✨ Dùng đề xuất cho tất cả</button></div>
        <h2>{currentQuestion.question}</h2>
        <p className="muted clarification-hint">Chọn nhanh một phương án; iDONE sẽ phân tích lại và dừng hỏi ngay khi đã đủ thông tin.</p>

        <div className="choice-chips">
          {currentQuestion.options.map((option, index) => <button key={option} type="button" className={index === 0 ? 'choice-chip recommended' : 'choice-chip'}
            onClick={() => void persistClarification(currentQuestion.id, option, index === 0 ? 'suggestion' : 'user')}>
            {index === 0 && <span>✨ Đề xuất</span>}{option}
          </button>)}
        </div>
        <div className="clarification-secondary-actions">
          <button type="button" className="ghost" onClick={() => void persistClarification(currentQuestion.id, currentQuestion.suggestion, 'auto')}>iDONE chọn giúp câu này</button>
          <button type="button" className="ghost" onClick={() => setShowCustomClarification(true)}>Tôi muốn nhập khác…</button>
        </div>

        {showCustomClarification && <form className="custom-answer" onSubmit={submitCustomClarification}>
          <label>Câu trả lời của bạn</label>
          <textarea rows={4} autoFocus value={customClarification} onChange={e => setCustomClarification(e.target.value)} placeholder="Viết theo cách của bạn…" />
          <div className="custom-answer-actions"><button type="button" className="ghost" onClick={() => { setShowCustomClarification(false); setCustomClarification('') }}>Hủy</button>
            <button className="primary" disabled={!customClarification.trim()}>Tiếp tục</button></div>
        </form>}

        <details className="why-question"><summary>Vì sao iDONE hỏi?</summary><p>{currentQuestion.reason}</p></details>
      </section> : <article className="notice success simple-notice">Đã đủ thông tin để bắt đầu. iDONE tự xử lý phần phân tích và kế hoạch ở phía sau.</article>}
    </div>}

    {simpleView === 'doing' && <div className="simple-stage">
      <article className="card autonomous-card">
        <div className="autonomous-orb" aria-hidden="true">{executorRunning ? '…' : 'i'}</div>
        <div><p className="eyebrow">iDONE đang xử lý</p><h2>{executorRunning ? 'Đang tạo kết quả cho bạn…' : nextActionLabel(work.lifecycle_state)}</h2>
          <p className="muted">iDONE tự hiểu yêu cầu, thực hiện và kiểm tra ở phía sau. Bạn không cần nhập WorkSpec hay dán kết quả thủ công.</p>
          <div className="micro-progress"><span>Hiểu yêu cầu</span><span>Thực hiện</span><span>Kiểm tra</span></div>
          {analysis.isRecurring && <p className="schedule-line"><strong>Lịch nhận biết:</strong> {analysis.scheduleHint ?? 'Định kỳ'}</p>}
        </div>
      </article>

      {executorError && <article className="notice warning simple-notice"><strong>Chưa thể tự xử lý:</strong> {executorError}
        <div className="retry-row"><button className="primary" disabled={executorRunning} onClick={() => void runAutonomousExecutor()}>Thử lại</button></div>
      </article>}

      {work.lifecycle_state === 'BLOCKED' && !executorError && <button className="primary" disabled={executorRunning} onClick={() => void runAutonomousExecutor()}>Tiếp tục xử lý</button>}
      {work.lifecycle_state === 'VERIFICATION_FAILED' && <button className="primary" disabled={executorRunning} onClick={() => void runAutonomousExecutor()}>Thực hiện bản sửa</button>}

      {['READY_FOR_VERIFICATION','AWAITING_APPROVAL','VERIFIED_DONE'].includes(work.lifecycle_state) && <article className="card result-ready-card"><h2>Kết quả đã sẵn sàng.</h2><button className="primary" onClick={() => setSimpleView('result')}>Xem kết quả</button></article>}
    </div>}

    {simpleView === 'result' && <div className="simple-stage">
      <article className="card result-card">
        <p className="eyebrow">{analysis.isRecurring ? 'Báo cáo mới nhất' : 'Kết quả iDONE'}</p>
        {resultText ? <div className="result-output">{resultText}</div> : <div className="empty-state compact"><h3>Chưa có kết quả cuối cùng.</h3><p>{nextActionLabel(work.lifecycle_state)}</p></div>}
        {Boolean(latestArtifact?.url_ref) && !analysis.isRecurring && <p className="artifact-line"><strong>File/link:</strong> {String(latestArtifact?.url_ref)}</p>}
      </article>
      {analysis.isRecurring && <article className="card automation-status-card">
        <div className="automation-runtime-heading">
          <div><p className="eyebrow">Nhân sự AI</p><h2>{automationJob?.status === 'ACTIVE' ? 'Đã kích hoạt' : automationJob?.status === 'PAUSED' ? 'Đang tạm dừng' : 'Chưa kích hoạt'}</h2>
            <p className="muted">{automationRuntimeReady ? 'Scheduler và lần chạy thử đã được xác nhận bằng runtime thực tế.' : 'iDONE đang chờ đủ bằng chứng runtime trước khi cho phép duyệt hoàn thành.'}</p></div>
          <span className={automationRuntimeReady ? 'runtime-badge ready' : 'runtime-badge pending'}>{automationRuntimeReady ? 'RUNTIME OK' : 'CHỜ RUNTIME'}</span>
        </div>
        <div className="automation-status-grid">
          <div><span>Lịch</span><strong>{analysis.scheduleHint ?? 'Định kỳ'}</strong></div>
          <div><span>Lần chạy tiếp theo</span><strong>{formatRuntimeDate(automationJob?.next_run_at)}</strong></div>
          <div><span>Lần chạy gần nhất</span><strong>{formatRuntimeDate(automationJob?.last_run_at)}</strong></div>
          <div><span>Trạng thái gần nhất</span><strong>{automationJob?.last_status === 'COMPLETED' ? 'Thành công' : automationJob?.last_status === 'FAILED' ? 'Thất bại' : 'Chờ lượt đầu tiên'}</strong></div>
        </div>
        <div className="automation-actions">
          {automationJob?.status === 'ACTIVE' ? <button className="secondary" onClick={() => void pauseAutomation()}>Tạm dừng</button> :
            Boolean(automationJob?.id) ? <button className="primary" onClick={() => void resumeAutomation()}>Bật lại</button> : null}
          {Boolean(automationJob?.id) && <button className="secondary" onClick={() => void requestAutomationRunNow()}>Chạy thử ngay</button>}
        </div>
      </article>}

      {work.lifecycle_state === 'READY_FOR_VERIFICATION' && <article className="card stack founder-review">
        <p className="eyebrow">Kiểm tra trong 10 giây</p><h2>Kết quả này có đúng điều bạn cần?</h2>
        {analysis.isRecurring && !automationRuntimeReady && <p className="notice warning">Chưa đủ điều kiện duyệt: cần lịch ACTIVE, lần chạy thử thành công và thời điểm chạy tiếp theo.</p>}
        <label className="check-row"><input type="checkbox" checked={factualChecked} onChange={e => setFactualChecked(e.target.checked)} /> Tôi đã kiểm tra các thông tin thực tế quan trọng.</label>
        <label className="check-row"><input type="checkbox" checked={acceptanceChecked} onChange={e => setAcceptanceChecked(e.target.checked)} /> Kết quả đáp ứng yêu cầu và đầu ra mong đợi.</label>
        <div className="action-row"><button className="primary" onClick={() => void verifySimple()}>Đạt — chuyển sang duyệt</button><button className="secondary" onClick={() => void requestSimpleFix()}>Chưa đạt — yêu cầu sửa</button></div>
      </article>}

      {work.lifecycle_state === 'AWAITING_APPROVAL' && <article className="card stack final-approval">
        <p className="eyebrow">Quyết định cuối</p><h2>Duyệt công việc hoàn thành?</h2>
        <p className="muted">iDONE đã lưu kết quả, evidence và verification. Im lặng không được coi là duyệt.</p>
        <div className="action-row"><button className="primary" onClick={() => void approveSimple()}>Duyệt hoàn thành</button><button className="secondary" onClick={() => void rejectSimple()}>Yêu cầu sửa</button></div>
      </article>}

      {work.lifecycle_state === 'VERIFIED_DONE' && <article className="card done-card"><div className="done-mark">✓</div><div><p className="eyebrow">Verified DONE</p><h2>Công việc đã hoàn thành.</h2><p className="muted">Kết quả, kiểm chứng và lịch sử đã được lưu.</p><button className="secondary" onClick={() => void exportSimple()}>Xuất gói kết quả</button></div></article>}

      {work.lifecycle_state === 'VERIFICATION_FAILED' && <article className="notice warning simple-notice">Kết quả cần sửa. <button className="ghost" onClick={() => setSimpleView('doing')}>Quay lại Đang làm</button></article>}
    </div>}

    {showAudit && <details className="advanced-panel auditor-only">
      <summary>Dành cho kiểm định viên</summary>
      <p className="muted small">Nhật ký kỹ thuật chỉ hiển thị khi mở iDONE với chế độ audit.</p>
      <div className="mvp-tabs">{tabs.map(([id,label]) => <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}</button>)}</div>
      {tab === 'spec' && <SpecStep client={client} user={user} workspace={workspace} work={work} records={records.specs} onWork={setWork} transition={transition} reload={load} setFlash={setFlash} />}
      {tab === 'plan' && <PlanStep client={client} user={user} workspace={workspace} work={work} records={records.plans} onWork={setWork} transition={transition} reload={load} setFlash={setFlash} />}
      {tab === 'execute' && <ExecutionStep client={client} user={user} workspace={workspace} work={work} records={records} transition={transition} reload={load} setFlash={setFlash} />}
      {tab === 'verify' && <VerifyStep client={client} user={user} workspace={workspace} work={work} records={records} transition={transition} reload={load} setFlash={setFlash} />}
      {tab === 'done' && <DoneStep client={client} user={user} workspace={workspace} work={work} gate={gate} transition={transition} reload={load} setFlash={setFlash} />}
      {tab === 'history' && <HistoryStep client={client} user={user} workspace={workspace} work={work} records={records} gate={gate} reload={load} setFlash={setFlash} />}
    </details>}
  </section>
}

type StepProps = { client: SupabaseClient; user: User; workspace: Workspace; work: WorkItem; reload: () => Promise<void>; setFlash: (f: Flash) => void; transition: (to: string, reason: string) => Promise<boolean> }

function SpecStep(p: StepProps & { records: Row[]; onWork: (w: WorkItem) => void }) {
  const [form, setForm] = useState(emptySpec(p.work.raw_intent))
  useEffect(() => {
    const current = p.records.find(row => row.id === p.work.current_work_spec_version_id) ?? p.records[0]
    const payload = current?.structured_payload
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return
    const workSpec = (payload as Record<string, unknown>).work_spec
    if (!workSpec || typeof workSpec !== 'object' || Array.isArray(workSpec)) return
    setForm(v => ({ ...v, ...(workSpec as Partial<typeof v>) }))
  }, [p.records, p.work.current_work_spec_version_id])
  const assessment = useMemo(() => assessClarification(form), [form])
  const summary = useMemo(() => buildClarificationSummary(form), [form])
  function field(key: keyof typeof form) { return { value: form[key], onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setForm(v => ({ ...v, [key]: e.target.value })) } }
  async function save() {
    if (!canEditClarificationState(p.work.lifecycle_state)) { p.setFlash({ kind: 'warning', text: 'WorkSpec đã khóa sau khi execution bắt đầu. Tạo work item mới hoặc remediation record thay vì sửa ngược requirement.' }); return }
    if (!assessment.pass || !form.acceptance.trim()) {
      if (p.work.lifecycle_state === 'DRAFT') await p.transition('NEEDS_CLARIFICATION', 'D-033 material gaps remain')
      p.setFlash({ kind: 'warning', text: 'Cần đủ 6 nhóm D-033 và ít nhất một tiêu chí nghiệm thu.' }); return
    }
    const latest = await p.client.from('work_spec_versions').select('version_no').eq('work_item_id', p.work.id).order('version_no', { ascending: false }).limit(1)
    if (latest.error) { p.setFlash({ kind: 'error', text: latest.error.message }); return }
    const version = await p.client.from('work_spec_versions').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, version_no: (latest.data?.[0]?.version_no ?? 0) + 1, structured_payload: { schema_version: 1, work_spec: form, clarification: { draft: form, assessment } }, created_by: p.user.id }).select('id').single()
    if (version.error) { p.setFlash({ kind: 'error', text: version.error.message }); return }
    const updated = await p.client.from('work_items').update({ current_work_spec_version_id: version.data.id }).eq('id', p.work.id).eq('workspace_id', p.workspace.id)
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
    if (updated.error) { p.setFlash({ kind: 'error', text: 'Đã lưu version nhưng chưa đổi current: ' + updated.error.message }); return }
    p.onWork(updated.data as WorkItem)
    await p.transition('READY', 'WorkSpec + D-033 PASS'); await p.reload()
    p.setFlash({ kind: 'success', text: 'WorkSpec version mới đã lưu; lịch sử cũ được giữ nguyên.' })
  }
  const labels: Array<[keyof typeof form,string]> = [['goal','Mục tiêu'],['audience','Đối tượng'],['scope','Phạm vi'],['data','Dữ liệu/nguồn'],['constraints','Ràng buộc'],['output','Đầu ra'],['acceptance','Tiêu chí nghiệm thu'],['dependencies','Phụ thuộc'],['assumptions','Giả định — Cần xác nhận'],['unknowns','KHÔNG RÕ'],['approvals','Duyệt bắt buộc'],['outOfScope','Ngoài phạm vi']]
  return <div><article className="card"><p className="eyebrow">D-033 + TC-042</p><h2>WorkSpec có version</h2><p className="muted">Không ghi đè im lặng. Mỗi lần lưu tạo một version mới.</p></article>
    <div className="form-grid">{labels.map(([key,label]) => <label className="card stack" key={key}><span>{label}</span><textarea rows={3} {...field(key)} /></label>)}</div>
    {!assessment.pass && <article className="card"><p className="notice warning">Còn thiếu: {assessment.missing.join(', ')}. Lượt hỏi đầu tối đa {assessment.questions.length}/5.</p>
      <ol className="clarification-list">{assessment.questions.map(q => <li key={q.field}><strong>{q.question}</strong><span>{q.reason}</span></li>)}</ol></article>}
    <article className="card summary-card"><h3>Tóm tắt trước thực thi</h3><p><strong>Đã hiểu:</strong> {summary.understood.join(' · ') || 'KHÔNG RÕ'}</p>
      <p><strong>Giả định còn lại:</strong> {summary.assumptions.join(' · ') || 'Không có'}</p>
      <p><strong>Phạm vi không làm:</strong> {summary.outOfScope.join(' · ')}</p>
      <p><strong>Đầu ra dự kiến:</strong> {summary.expectedOutput}</p></article>
    <button className="primary" disabled={!canEditClarificationState(p.work.lifecycle_state)} onClick={() => void save()}>Lưu WorkSpec & chuyển READY</button></div>
}

function PlanStep(p: StepProps & { records: Row[]; onWork: (w: WorkItem) => void }) {
  const [steps, setSteps] = useState('1. Chuẩn bị\n2. Thực hiện\n3. Kiểm tra')
  const [tools, setTools] = useState('Chat / Work / Codex')
  const [checkpoints, setCheckpoints] = useState('D-032; D-033; DoD')
  useEffect(() => {
    const current = p.records.find(row => row.id === p.work.current_route_plan_version_id) ?? p.records[0]
    const payload = current?.structured_payload
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return
    const data = payload as Record<string, unknown>
    if (Array.isArray(data.steps)) setSteps(data.steps.map(String).join('\n'))
    if (typeof data.tools === 'string') setTools(data.tools)
    if (typeof data.checkpoints === 'string') setCheckpoints(data.checkpoints)
  }, [p.records, p.work.current_route_plan_version_id])
  async function save() {
    if (!p.work.current_work_spec_version_id) { p.setFlash({ kind: 'warning', text: 'Phải có WorkSpec trước.' }); return }
    const latest = await p.client.from('route_plan_versions').select('version_no').eq('work_item_id', p.work.id).order('version_no', { ascending: false }).limit(1)
    if (latest.error) { p.setFlash({ kind: 'error', text: latest.error.message }); return }
    const result = await p.client.from('route_plan_versions').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, version_no: (latest.data?.[0]?.version_no ?? 0) + 1, structured_payload: { steps: steps.split('\n').filter(Boolean), tools, checkpoints, founder_gates: 'FG-01..FG-11' }, created_by: p.user.id }).select('id').single()
    if (result.error) { p.setFlash({ kind: 'error', text: result.error.message }); return }
    const updated = await p.client.from('work_items').update({ current_route_plan_version_id: result.data.id }).eq('id', p.work.id).eq('workspace_id', p.workspace.id)
      .select('id,title,raw_intent,lifecycle_state,current_work_spec_version_id,current_route_plan_version_id,updated_at').single()
    if (updated.error) { p.setFlash({ kind: 'error', text: updated.error.message }); return }
    p.onWork(updated.data as WorkItem); await p.reload(); p.setFlash({ kind: 'success', text: 'Route/Plan version mới đã lưu.' })
  }
  return <div className="form-grid"><label className="card stack"><span>Các bước — mỗi dòng một bước</span><textarea rows={8} value={steps} onChange={e => setSteps(e.target.value)} /></label>
    <label className="card stack"><span>Tools / execution type</span><textarea rows={3} value={tools} onChange={e => setTools(e.target.value)} /><span>Checkpoints</span><textarea rows={3} value={checkpoints} onChange={e => setCheckpoints(e.target.value)} /><button className="primary" onClick={() => void save()}>Lưu Route/Plan</button></label></div>
}

function ExecutionStep(p: StepProps & { records: { executions: Row[]; artifacts: Row[] } }) {
  const [type, setType] = useState('WORK'); const [notes, setNotes] = useState(''); const [artifact, setArtifact] = useState(''); const [status, setStatus] = useState('COMPLETED')
  async function add(event: FormEvent) {
    event.preventDefault()
    if (!p.work.current_route_plan_version_id) { p.setFlash({ kind: 'warning', text: 'Phải lưu Route/Plan trước.' }); return }
    if (['READY', 'VERIFICATION_FAILED', 'BLOCKED'].includes(p.work.lifecycle_state)) {
      await p.transition('IN_PROGRESS', 'Execution started or remediation resumed')
    }
    const result = await p.client.from('execution_records').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, route_plan_version_id: p.work.current_route_plan_version_id, execution_type: type, actor_user_id: p.user.id, capability: type, status, started_at: new Date().toISOString(), completed_at: status === 'COMPLETED' ? new Date().toISOString() : null, notes }).select('id').single()
    if (result.error) { p.setFlash({ kind: 'error', text: result.error.message }); return }
    if (artifact.trim()) {
      const artifactResult = await p.client.from('artifact_references').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, execution_record_id: result.data.id, url_ref: artifact.trim(), metadata: { label: 'Execution output' }, created_by: p.user.id })
      if (artifactResult.error) { p.setFlash({ kind: 'error', text: 'Execution đã ghi nhưng artifact reference thất bại: ' + artifactResult.error.message }); return }
    }
    if (status === 'COMPLETED') await p.transition('READY_FOR_VERIFICATION', 'Execution output recorded')
    if (status === 'FAILED' || status === 'BLOCKED') await p.transition('BLOCKED', `Execution ${status}`)
    await p.reload(); setNotes(''); setArtifact(''); p.setFlash({ kind: 'success', text: 'Execution record đã được ghi.' })
  }
  return <div><form className="card stack" onSubmit={add}><h2>Ghi execution</h2><label>Loại</label><select value={type} onChange={e => setType(e.target.value)}><option>MANUAL</option><option>CHAT</option><option>WORK</option><option>CODEX</option></select>
    <label>Trạng thái</label><select value={status} onChange={e => setStatus(e.target.value)}><option>STARTED</option><option>COMPLETED</option><option>FAILED</option><option>BLOCKED</option></select>
    <label>Kết quả / ghi chú / lỗi</label><textarea required rows={6} value={notes} onChange={e => setNotes(e.target.value)} />
    <label>Artifact URL / reference (nếu có)</label><input value={artifact} onChange={e => setArtifact(e.target.value)} placeholder="https://… hoặc đường dẫn tham chiếu" />
    <button className="primary">Ghi execution</button></form>
    <RecordList title="Execution records" rows={p.records.executions} /><RecordList title="Artifacts" rows={p.records.artifacts} /></div>
}

function VerifyStep(p: StepProps & { records: { evidence: Row[]; verifications: Row[]; defects: Row[] } }) {
  const [source, setSource] = useState(''); const [text, setText] = useState(''); const [evidenceType, setEvidenceType] = useState('DỮ KIỆN'); const [provenance, setProvenance] = useState('SOURCE-SUPPORTED')
  const [criterion, setCriterion] = useState('DoD'); const [result, setResult] = useState('PASS'); const [comments, setComments] = useState(''); const [factual, setFactual] = useState(false); const [severity, setSeverity] = useState('Major')
  async function evidence(event: FormEvent) {
    event.preventDefault()
    const res = await p.client.from('evidence_records').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, evidence_type: evidenceType, provenance_status: provenance, source_locator: source, reference_payload: { text }, uncertainty_status: text.includes('KHÔNG RÕ') ? 'KHÔNG RÕ' : 'KNOWN', producer_user_id: p.user.id })
    if (res.error) p.setFlash({ kind: 'error', text: res.error.message }); else { setSource(''); setText(''); await p.reload(); p.setFlash({ kind: 'success', text: 'Evidence đã gắn.' }) }
  }
  async function verify(event: FormEvent) {
    event.preventDefault()
    const ref = factual ? 'D-032' : criterion
    const evidenceRefs = p.records.evidence.map(row => String(row.id ?? '')).filter(Boolean)
    if (!evidenceRefs.length) { p.setFlash({ kind: 'warning', text: 'Phải có ít nhất một evidence trước khi verification.' }); return }
    const res = await p.client.from('verification_records').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, criterion_ref: ref, result, evidence_refs: evidenceRefs, verifier_user_id: p.user.id, verifier_capability: 'INDEPENDENT_VERIFIER', comments }).select('id').single()
    if (res.error) { p.setFlash({ kind: 'error', text: res.error.message }); return }
    if (result === 'FAIL') {
      const defect = await p.client.from('defects').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, verification_record_id: res.data.id, severity, state: 'OPEN', description: comments || `Verification ${ref} failed` })
      if (defect.error) { p.setFlash({ kind: 'error', text: defect.error.message }); return }
      await p.transition('VERIFICATION_FAILED', 'Verification FAIL')
    } else if (result === 'PASS' && p.work.lifecycle_state === 'READY_FOR_VERIFICATION') {
      const combined = [...p.records.verifications, { criterion_ref: ref, result }]
      const hasFactual = combined.some(r => r.criterion_ref === 'D-032' && r.result === 'PASS')
      const hasAcceptance = combined.some(r => r.criterion_ref !== 'D-032' && r.result === 'PASS')
      const hasBlockingResult = combined.some(r => r.result === 'FAIL' || r.result === 'NOT CHECKABLE')
      if (hasFactual && hasAcceptance && !hasBlockingResult) await p.transition('AWAITING_APPROVAL', 'Verification criteria PASS')
    }
    await p.reload(); p.setFlash({ kind: 'success', text: 'Verification record đã ghi; self-claim không được dùng thay verification.' })
  }
  async function resolve(id: unknown) {
    const res = await p.client.from('defects').update({ state: 'RESOLVED', resolution_ref: 'Resolved in MVP review' }).eq('id', String(id)).eq('workspace_id', p.workspace.id)
    if (res.error) p.setFlash({ kind: 'error', text: res.error.message }); else { await p.reload(); p.setFlash({ kind: 'success', text: 'Defect đã resolved.' }) }
  }
  async function waive(id: unknown) {
    const reason = window.prompt('Nhập waiver reference / lý do được duyệt:')
    if (!reason?.trim()) return
    const res = await p.client.from('defects').update({ state: 'WAIVED', waiver_ref: reason.trim() }).eq('id', String(id)).eq('workspace_id', p.workspace.id)
    if (res.error) p.setFlash({ kind: 'error', text: res.error.message }); else { await p.reload(); p.setFlash({ kind: 'success', text: 'Defect đã được waiver có ghi lý do.' }) }
  }
  return <div className="two-column"><form className="card stack" onSubmit={evidence}><h2>Evidence</h2>
      <label>Loại bằng chứng</label><select value={evidenceType} onChange={e => setEvidenceType(e.target.value)}><option>DỮ KIỆN</option><option>SUY LUẬN</option><option>GIẢ THUYẾT</option><option>KHUYẾN NGHỊ</option></select>
      <label>Provenance</label><select value={provenance} onChange={e => setProvenance(e.target.value)}><option>SOURCE-SUPPORTED</option><option>USER-PROVIDED</option><option>SYSTEM-GENERATED</option><option>UNVERIFIED</option></select>
      <label>Source locator</label><input required value={source} onChange={e => setSource(e.target.value)} /><label>Nội dung/tham chiếu</label><textarea required rows={5} value={text} onChange={e => setText(e.target.value)} /><button className="primary">Gắn evidence</button><RecordList title="Đã gắn" rows={p.records.evidence} /></form>
    <div><form className="card stack" onSubmit={verify}><h2>Verification độc lập</h2><p className="muted small">Tối thiểu cần PASS cho D-032 và một tiêu chí nghiệm thu/DoD trước khi chuyển sang duyệt.</p>
      <label><input type="checkbox" checked={factual} onChange={e => setFactual(e.target.checked)} /> Kiểm D-032 factual integrity</label><input value={criterion} disabled={factual} onChange={e => setCriterion(e.target.value)} />
      <label>Kết quả</label><select value={result} onChange={e => setResult(e.target.value)}><option>PASS</option><option>FAIL</option><option>NOT CHECKABLE</option><option>N/A</option></select>
      {result === 'FAIL' && <><label>Mức độ defect</label><select value={severity} onChange={e => setSeverity(e.target.value)}><option>Blocker</option><option>Critical</option><option>Major</option><option>Minor</option></select></>}
      <label>Nhận xét / remediation</label><textarea rows={4} value={comments} onChange={e => setComments(e.target.value)} /><button className="primary">Ghi verification</button></form>
      <RecordList title="Verification" rows={p.records.verifications} /><div className="card"><h3>Defects</h3>{p.records.defects.length ? p.records.defects.map(r => <p key={String(r.id)}><Badge value={String(r.severity)} /> {String(r.description)} · {String(r.state)} {r.state === 'OPEN' && <span className="inline-actions"><button className="ghost" onClick={() => void resolve(r.id)}>Resolve</button>{String(r.severity) === 'Major' && <button className="ghost" onClick={() => void waive(r.id)}>Waive</button>}</span>}</p>) : <p className="muted">Chưa có defect.</p>}</div></div></div>
}

function DoneStep(p: StepProps & { gate: { pass: boolean; missing: string[] } }) {
  const [source, setSource] = useState('Founder action in iDONE')
  async function approve() {
    const blockers = p.gate.missing.filter(x => x !== 'Thiếu approval rõ ràng.')
    if (blockers.length) { p.setFlash({ kind: 'warning', text: 'Chưa thể DONE: ' + blockers.join(' ') }); return }
    const res = await p.client.from('approval_records').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, target_type: 'WORK_ITEM', target_id: p.work.id, approver_user_id: p.user.id, decision: 'APPROVED', scope: 'MVP work item completion', source_locator: source })
    if (res.error) { p.setFlash({ kind: 'error', text: res.error.message }); return }
    const ok = await p.transition('VERIFIED_DONE', 'Founder approval + DONE gate PASS')
    if (ok) { await p.reload(); p.setFlash({ kind: 'success', text: 'VERIFIED DONE — approval và audit đã ghi.' }) }
  }
  async function reject() {
    const reason = window.prompt('Lý do không duyệt:')
    if (!reason?.trim()) return
    const res = await p.client.from('approval_records').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, target_type: 'WORK_ITEM', target_id: p.work.id, approver_user_id: p.user.id, decision: 'NOT APPROVED', scope: reason.trim(), source_locator: source })
    if (res.error) { p.setFlash({ kind: 'error', text: res.error.message }); return }
    const ok = await p.transition('VERIFICATION_FAILED', 'Founder rejected approval')
    if (ok) { await p.reload(); p.setFlash({ kind: 'warning', text: 'Không duyệt. Công việc đã quay lại VERIFICATION FAILED để sửa.' }) }
  }
  return <div className="two-column"><article className="card"><p className="eyebrow">DoD gate</p><h2>{p.gate.missing.length === 1 && p.gate.missing[0] === 'Thiếu approval rõ ràng.' ? 'Sẵn sàng duyệt' : p.gate.pass ? 'PASS' : 'Chưa đạt'}</h2>
    {p.gate.missing.length ? <ul>{p.gate.missing.map(x => <li key={x}>{x}</li>)}</ul> : <p className="notice success">Mọi điều kiện đã đạt.</p>}</article>
    <article className="card stack"><h2>Approval rõ ràng</h2><label>Source locator</label><input value={source} onChange={e => setSource(e.target.value)} /><button className="primary" disabled={p.work.lifecycle_state !== 'AWAITING_APPROVAL'} onClick={() => void approve()}>Duyệt & đánh dấu VERIFIED DONE</button><button className="secondary" disabled={p.work.lifecycle_state !== 'AWAITING_APPROVAL'} onClick={() => void reject()}>Không duyệt</button><p className="muted small">Im lặng không được coi là approval.</p></article></div>
}

function HistoryStep(p: Omit<StepProps,'transition'> & { records: { specs: Row[]; plans: Row[]; executions: Row[]; artifacts: Row[]; evidence: Row[]; verifications: Row[]; defects: Row[]; approvals: Row[]; transitions: Row[]; decisions: Row[] }; gate: { pass: boolean; missing: string[] } }) {
  const [decisionId, setDecisionId] = useState('')
  async function addDecision(event: FormEvent) {
    event.preventDefault()
    const value = decisionId.trim()
    if (!value) return
    const res = await p.client.from('decision_links').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, decision_id: value, relation_type: 'GOVERNS' })
    if (res.error) p.setFlash({ kind: 'error', text: res.error.message }); else { setDecisionId(''); await p.reload(); p.setFlash({ kind: 'success', text: 'Decision link đã được ghi.' }) }
  }
  async function exportData() {
    const payload = buildPortableExport({ work_item: p.work, work_specs: p.records.specs, route_plans: p.records.plans, executions: p.records.executions, artifacts: p.records.artifacts, evidence: p.records.evidence, verifications: p.records.verifications, defects: p.records.defects, approvals: p.records.approvals, transitions: p.records.transitions, decisions: p.records.decisions, done_gate: p.gate })
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `idone-${p.work.id}.json`; anchor.click(); URL.revokeObjectURL(url)
    const logged = await p.client.from('export_packages').insert({ workspace_id: p.workspace.id, work_item_id: p.work.id, format: 'JSON', created_by: p.user.id })
    if (logged.error) p.setFlash({ kind: 'warning', text: 'File đã tải nhưng không ghi được export log: ' + logged.error.message }); else { await p.reload(); p.setFlash({ kind: 'success', text: 'Đã xuất package JSON portable và ghi export log.' }) }
  }
  return <div><div className="history-actions"><button className="primary" onClick={() => void exportData()}>Xuất handover JSON</button>
    <form className="decision-link-form" onSubmit={addDecision}><input value={decisionId} onChange={e => setDecisionId(e.target.value)} placeholder="Decision ID, ví dụ D-041" /><button className="secondary">Gắn decision</button></form></div>
    <div className="two-column">
    <RecordList title="State transitions" rows={p.records.transitions} /><RecordList title="Decision links" rows={p.records.decisions} />
    <RecordList title="WorkSpec versions" rows={p.records.specs} /><RecordList title="Plan versions" rows={p.records.plans} />
    <RecordList title="Execution records" rows={p.records.executions} /><RecordList title="Artifacts" rows={p.records.artifacts} />
    <RecordList title="Evidence" rows={p.records.evidence} /><RecordList title="Verification" rows={p.records.verifications} />
    <RecordList title="Defects" rows={p.records.defects} /><RecordList title="Approvals" rows={p.records.approvals} /></div></div>
}

function RecordList({ title, rows }: { title: string; rows: Row[] }) {
  return <article className="card record-list"><h3>{title}</h3>{rows.length ? rows.slice(0, 20).map((row, index) => <details key={String(row.id ?? index)}><summary>{String(row.result ?? row.status ?? row.decision ?? row.to_state ?? ('Bản ghi ' + (index + 1)))}</summary><pre>{JSON.stringify(row, null, 2)}</pre></details>) : <p className="muted">Chưa có dữ liệu.</p>}</article>
}
function Badge({ value }: { value: string }) { return <span className="state-badge">{value.replaceAll('_',' ')}</span> }
function FlashView({ flash }: { flash: Flash }) { return flash ? <p className={`notice ${flash.kind}`}>{flash.text}</p> : null }
function Status({ title, detail }: { title: string; detail?: string }) { return <main className="center-shell"><section className="card narrow-card"><p className="eyebrow">iDONE</p><h1>{title}</h1>{detail && <p>{detail}</p>}</section></main> }
